/* Shades of India — customer account pages.
 * Sign-in tabs, mobile/email OTP, Google sign-in and the address book.
 * The OTP and Google endpoints live in the Shades Control Panel app and are reached through the
 * Shopify app proxy (/apps/soi/...). Once the app has verified the customer it hands back a
 * single-use password for that customer, which we immediately spend on Shopify's own login form —
 * that is what creates the real customer session.
 */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const root = $('[data-soi-account]');
  if (!root) return;

  // The image panel is sized to the visible screen: viewport height minus whatever sits above this section
  // (announcement bar + header), and it sticks just under the sticky header while the form scrolls.
  const setOffsets = () => {
    const hdr = document.querySelector('[data-soi-dheader] .site-header, [data-soi-dheader] header');
    root.style.setProperty('--soi-acc-top', `${Math.round(root.getBoundingClientRect().top + window.scrollY)}px`);
    root.style.setProperty('--soi-acc-sticky', `${Math.round(hdr ? hdr.getBoundingClientRect().height : 70)}px`);
  };
  setOffsets();
  window.addEventListener('load', setOffsets);
  window.addEventListener('resize', setOffsets);

  /* ---------- Sign-in method: Mobile OTP (default) or Email ---------- */
  let method = 'otp';
  const currentTab = () => $('[data-soi-auth-tab].is-active', root)?.dataset.soiAuthTab || 'signin';
  const syncView = () => {
    const tabName = currentTab();
    const signup = tabName === 'signup';
    const otpBox = $('[data-soi-otp]', root);
    if (otpBox) otpBox.hidden = signup || method !== 'otp';
    const bar = $('[data-soi-methods]', root);
    if (bar) bar.hidden = signup;
    $$('[data-soi-auth-pane]', root).forEach((p) => {
      p.hidden = p.dataset.soiAuthPane === 'signup' ? !signup : (signup || method !== 'email');
    });
    $$('[data-soi-method]', root).forEach((b) => {
      const on = b.dataset.soiMethod === method;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', String(on));
    });
    $$('[data-soi-legal]', root).forEach((el) => { el.hidden = signup; });
    root.dataset.method = method;
  };
  // Short fade-out of the form that is leaving, then the other one fades in (see .soi-acc__enter / .soi-acc__leave).
  const leave = (el) => new Promise((resolve) => {
    if (!el || el.hidden) return resolve();
    el.classList.add('soi-acc__leave');
    setTimeout(() => { el.classList.remove('soi-acc__leave'); resolve(); }, 170);
  });
  $$('[data-soi-method]', root).forEach((b) => b.addEventListener('click', async () => {
    const next = b.dataset.soiMethod === 'email' ? 'email' : 'otp';
    if (next === method) return;
    const current = method === 'otp' ? $('[data-soi-otp]', root) : $('[data-soi-auth-pane="signin"]', root);
    await leave(current);
    method = next;
    syncView();
    const first = next === 'email' ? $('[data-soi-auth-pane="signin"] input[type="email"]', root) : $('[data-soi-otp-phone]', root);
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 120);
  }));
  // Anything that gets un-hidden inside the card (tabs, OTP steps, recover form, code boxes) eases in.
  if ('MutationObserver' in window) {
    new MutationObserver((muts) => muts.forEach((m) => {
      const el = m.target;
      if (m.attributeName === 'hidden' && !el.hidden && el.nodeType === 1) {
        el.classList.remove('soi-acc__enter');
        void el.offsetWidth;
        el.classList.add('soi-acc__enter');
      }
    })).observe(root, { attributes: true, subtree: true, attributeFilter: ['hidden'] });
  }

  /* ---------- Sign in / create account tabs ---------- */
  $$('[data-soi-auth-tab]', root).forEach((tab) => {
    tab.addEventListener('click', () => {
      const name = tab.dataset.soiAuthTab;
      $$('[data-soi-auth-tab]', root).forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
      syncView();
      const title = $('[data-soi-auth-title]', root);
      const sub = $('[data-soi-auth-sub]', root);
      if (title) title.textContent = name === 'signup' ? 'Create your account' : 'Welcome back';
      if (sub) sub.textContent = name === 'signup'
        ? 'Save your favourites, track orders and check out faster.'
        : 'Sign in to track orders, save favourites and check out faster.';
      $('[data-soi-recover]', root)?.setAttribute('hidden', '');
      $('[data-soi-auth-pane="signin"] form', root)?.removeAttribute('hidden');
    });
  });
  // The recover link only exists on the email form: opening it also selects that method.

  /* ---------- Forgot password ---------- */
  $('[data-soi-recover-open]', root)?.addEventListener('click', (e) => {
    e.preventDefault();
    $('[data-soi-recover]', root).hidden = false;
    $('[data-soi-auth-pane="signin"] form', root).hidden = true;
  });
  $('[data-soi-recover-close]', root)?.addEventListener('click', () => {
    $('[data-soi-recover]', root).hidden = true;
    $('[data-soi-auth-pane="signin"] form', root).hidden = false;
  });
  if (location.hash === '#recover') { method = 'email'; $('[data-soi-recover-open]', root)?.click(); }
  // A returned form error / reset notice belongs to the email form.
  if ($('.soi-acc__error', root) || $('.soi-acc__note', root)) method = 'email';

  /* ---------- Firebase (mobile code) + signup captcha ---------- */
  const fbCfg = {
    apiKey: root.dataset.fbApiKey, authDomain: root.dataset.fbAuthDomain,
    projectId: root.dataset.fbProjectId, appId: root.dataset.fbAppId,
  };
  const FB_BASE = 'https://www.gstatic.com/firebasejs/12.14.0/';
  let fbPromise = null;
  const loadFirebase = () => {
    if (!fbPromise) {
      fbPromise = Promise.all([import(`${FB_BASE}firebase-app.js`), import(`${FB_BASE}firebase-auth.js`)]).then(([appMod, authMod]) => {
        const app = appMod.getApps().find((a) => a.name === 'soi-account') || appMod.initializeApp(fbCfg, 'soi-account');
        return { auth: authMod.getAuth(app), RecaptchaVerifier: authMod.RecaptchaVerifier, signInWithPhoneNumber: authMod.signInWithPhoneNumber, signOut: authMod.signOut };
      });
      fbPromise.catch(() => { fbPromise = null; });
    }
    return fbPromise;
  };
  const fbError = (err) => {
    const map = {
      'auth/invalid-phone-number': 'That mobile number does not look right.',
      'auth/missing-phone-number': 'Enter your mobile number.',
      'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
      'auth/quota-exceeded': 'We cannot send codes right now. Please try again later.',
      'auth/captcha-check-failed': 'Please complete the security check and try again.',
      'auth/invalid-verification-code': 'That code is not right. Please check and try again.',
      'auth/code-expired': 'That code has expired. Please ask for a new one.',
      'auth/network-request-failed': 'Network problem. Please check your connection and try again.',
      'auth/operation-not-allowed': 'Mobile sign-in is not switched on yet.',
      'auth/unauthorized-domain': 'Mobile sign-in is not enabled for this address yet.',
    };
    return map[err && err.code] || (err && err.message && !/^Firebase/.test(err.message) ? err.message : 'Something went wrong. Please try again.');
  };
  const captchaOn = root.dataset.captcha === 'true';
  const captchas = {}; // name -> { verifier, widgetId, solved }
  const gates = []; // callbacks run whenever a captcha state changes
  const renderCaptcha = async (name, size) => {
    if (captchas[name]) return captchas[name];
    const host = $(`[data-soi-captcha="${name}"]`, root);
    if (!host) return null;
    const fb = await loadFirebase();
    const entry = { solved: false, widgetId: null, verifier: null };
    entry.verifier = new fb.RecaptchaVerifier(fb.auth, host, {
      size,
      callback: () => { entry.solved = true; gates.forEach((g) => g()); },
      'expired-callback': () => { entry.solved = false; gates.forEach((g) => g()); },
    });
    entry.widgetId = await entry.verifier.render();
    captchas[name] = entry;
    return entry;
  };
  const resetCaptcha = (name) => {
    const c = captchas[name];
    if (!c) return;
    c.solved = false;
    try { window.grecaptcha && window.grecaptcha.reset(c.widgetId); } catch (e) { /* widget gone */ }
    gates.forEach((g) => g());
  };
  const activeTab = () => $('[data-soi-auth-tab].is-active', root)?.dataset.soiAuthTab || 'signin';

  // Email signup: the visible check must be ticked before the button works. If the widget cannot load at all
  // (blocked network) we fail open after a few seconds — Shopify's own spam protection still screens the form.
  const signupGate = $('[data-soi-captcha-gate]', root);
  let emailCaptchaFailed = false;
  const syncEmailGate = () => {
    if (!signupGate) return;
    signupGate.disabled = captchaOn && !emailCaptchaFailed && !(captchas.email && captchas.email.solved);
  };
  gates.push(syncEmailGate);
  let emailCaptchaStarted = false;
  const startSignupCaptchas = () => {
    if (!captchaOn) return;
    if (signupGate && method === 'email' && !emailCaptchaStarted) {
      emailCaptchaStarted = true;
      syncEmailGate();
      renderCaptcha('email', 'normal').catch(() => { emailCaptchaFailed = true; syncEmailGate(); });
      setTimeout(() => { if (!captchas.email) { emailCaptchaFailed = true; syncEmailGate(); } }, 9000);
    }
    if (method === 'otp' && root.dataset.provider === 'firebase' && $('[data-soi-captcha="otp"]', root)) {
      renderCaptcha('otp', 'normal').catch(() => {});
    }
  };
  var ensureCaptchas = () => { if (activeTab() === 'signup') startSignupCaptchas(); };
  if (signupGate && captchaOn) signupGate.disabled = true;
  syncView();

  /* ---------- Country selector (flag + dial code) for mobile numbers ---------- */
  const COUNTRIES = [["India", "in", "+91"], ["United States", "us", "+1"], ["Canada", "ca", "+1"], ["United Kingdom", "gb", "+44"], ["United Arab Emirates", "ae", "+971"], ["Saudi Arabia", "sa", "+966"], ["Qatar", "qa", "+974"], ["Kuwait", "kw", "+965"], ["Oman", "om", "+968"], ["Bahrain", "bh", "+973"], ["Singapore", "sg", "+65"], ["Malaysia", "my", "+60"], ["Australia", "au", "+61"], ["New Zealand", "nz", "+64"], ["Germany", "de", "+49"], ["France", "fr", "+33"], ["Italy", "it", "+39"], ["Spain", "es", "+34"], ["Netherlands", "nl", "+31"], ["Switzerland", "ch", "+41"], ["Sweden", "se", "+46"], ["Norway", "no", "+47"], ["Denmark", "dk", "+45"], ["Finland", "fi", "+358"], ["Ireland", "ie", "+353"], ["Belgium", "be", "+32"], ["Austria", "at", "+43"], ["Portugal", "pt", "+351"], ["Greece", "gr", "+30"], ["Luxembourg", "lu", "+352"], ["Malta", "mt", "+356"], ["Cyprus", "cy", "+357"], ["Poland", "pl", "+48"], ["Czechia", "cz", "+420"], ["Hungary", "hu", "+36"], ["Romania", "ro", "+40"], ["Ukraine", "ua", "+380"], ["Turkey", "tr", "+90"], ["Russia", "ru", "+7"], ["Israel", "il", "+972"], ["Jordan", "jo", "+962"], ["Lebanon", "lb", "+961"], ["Iraq", "iq", "+964"], ["Iran", "ir", "+98"], ["Egypt", "eg", "+20"], ["Morocco", "ma", "+212"], ["Kenya", "ke", "+254"], ["Uganda", "ug", "+256"], ["Tanzania", "tz", "+255"], ["Ethiopia", "et", "+251"], ["Ghana", "gh", "+233"], ["Nigeria", "ng", "+234"], ["South Africa", "za", "+27"], ["Mauritius", "mu", "+230"], ["Japan", "jp", "+81"], ["South Korea", "kr", "+82"], ["China", "cn", "+86"], ["Hong Kong", "hk", "+852"], ["Taiwan", "tw", "+886"], ["Thailand", "th", "+66"], ["Vietnam", "vn", "+84"], ["Indonesia", "id", "+62"], ["Philippines", "ph", "+63"], ["Cambodia", "kh", "+855"], ["Myanmar", "mm", "+95"], ["Sri Lanka", "lk", "+94"], ["Nepal", "np", "+977"], ["Bangladesh", "bd", "+880"], ["Pakistan", "pk", "+92"], ["Afghanistan", "af", "+93"], ["Bhutan", "bt", "+975"], ["Maldives", "mv", "+960"], ["Fiji", "fj", "+679"], ["Brazil", "br", "+55"], ["Mexico", "mx", "+52"], ["Argentina", "ar", "+54"], ["Chile", "cl", "+56"], ["Colombia", "co", "+57"], ["Peru", "pe", "+51"]];
  const flagUrl = (iso) => `https://flagcdn.com/24x18/${iso}.png`;
  const setDial = (cc, c) => {
    const holder = cc.closest('[data-soi-otp], [data-soi-reg]');
    if (holder) { holder.dataset.dial = c[2]; holder.dataset.iso = c[1]; }
    $('[data-soi-cc-flag]', cc).src = flagUrl(c[1]);
    $('[data-soi-cc-flag]', cc).alt = c[0];
    $('[data-soi-cc-dial]', cc).textContent = c[2];
    // the number field itself (not the country search box that lives inside the dropdown)
    const input = cc.closest('.soi-acc__phone')?.querySelector(':scope > input');
    if (input) {
      input.maxLength = c[2] === '+91' ? 10 : 14;
      input.value = input.value.replace(/\D/g, '').slice(0, input.maxLength);
      input.placeholder = c[2] === '+91' ? '98765 43210' : 'Mobile number';
    }
  };
  $$('[data-soi-cc]', root).forEach((cc) => {
    const btn = $('[data-soi-cc-btn]', cc);
    const menu = $('.soi-cc__menu', cc);
    const list = $('[data-soi-cc-list]', cc);
    const search = $('[data-soi-cc-search]', cc);
    const draw = (q) => {
      const t = (q || '').trim().toLowerCase().replace(/^\+/, '');
      list.innerHTML = COUNTRIES.filter((c) => !t || c[0].toLowerCase().includes(t) || c[2].replace('+', '').startsWith(t) || c[1] === t)
        .map((c) => `<li role="option" tabindex="-1" data-iso="${c[1]}"><img src="${flagUrl(c[1])}" alt="" width="24" height="18" loading="lazy"><span class="soi-cc__name">${c[0]}</span><span class="soi-cc__code">${c[2]}</span></li>`).join('');
    };
    const open = (on) => {
      menu.hidden = !on;
      btn.setAttribute('aria-expanded', String(on));
      if (on) { search.value = ''; draw(''); setTimeout(() => search.focus({ preventScroll: true }), 30); }
    };
    btn.addEventListener('click', (e) => { e.preventDefault(); open(menu.hidden); });
    search.addEventListener('input', () => draw(search.value));
    list.addEventListener('click', (e) => {
      const li = e.target.closest('li[data-iso]');
      if (!li) return;
      const c = COUNTRIES.find((x) => x[1] === li.dataset.iso);
      if (c) setDial(cc, c);
      open(false);
      cc.closest('.soi-acc__phone')?.querySelector(':scope > input')?.focus();
    });
    document.addEventListener('click', (e) => { if (!menu.hidden && !cc.contains(e.target)) open(false); });
    cc.addEventListener('keydown', (e) => { if (e.key === 'Escape') { open(false); btn.focus(); } });
    const holder = cc.closest('[data-soi-otp], [data-soi-reg]');
    const start = COUNTRIES.find((c) => c[2] === (holder && holder.dataset.dial)) || COUNTRIES[0];
    setDial(cc, start);
  });

  /* ---------- Create account: mobile number verified with a one-time code (Firebase, no backend needed) ---------- */
  const reg = $('[data-soi-reg]', root);
  if (reg) {
    const getDial = () => reg.dataset.dial || '+91';
    const phone = $('[data-soi-reg-phone]', reg);
    const sendBtn = $('[data-soi-reg-send]', reg);
    const codeBox = $('[data-soi-reg-code]', reg);
    const digits = $$('[data-soi-reg-digit]', reg);
    const verifyBtn = $('[data-soi-reg-verify]', reg);
    const resendBtn = $('[data-soi-reg-resend]', reg);
    const changeBtn = $('[data-soi-reg-change]', reg);
    const timerEl = $('[data-soi-reg-timer]', reg);
    const okEl = $('[data-soi-reg-ok]', reg);
    const msgEl = $('[data-soi-reg-msg]', reg);
    const note = $('[data-soi-reg-note]', reg);
    const flag = $('[data-soi-reg-verified]', reg);
    const submit = $('[data-soi-signup-submit]', root);
    const form = $('[data-soi-signup-form]', root);
    let confirmation = null; let verified = false; let skipped = false; let target = ''; let timer;

    const say = (t, kind) => { msgEl.textContent = t || ''; msgEl.hidden = !t; msgEl.className = `soi-acc__msg${kind ? ' is-' + kind : ''}`; };
    const sync = () => { if (submit) submit.disabled = !(verified || skipped); };
    const tick = (n) => {
      clearInterval(timer);
      let left = n;
      const run = () => {
        if (left <= 0) { clearInterval(timer); resendBtn.disabled = false; timerEl.textContent = ''; return; }
        resendBtn.disabled = true; timerEl.textContent = `in ${left}s`; left -= 1;
      };
      run(); timer = setInterval(run, 1000);
    };
    const send = async () => {
      const raw = (phone.value || '').replace(/\D/g, '');
      const dial = getDial();
      const okLen = dial === '+91' ? /^\d{10}$/.test(raw) : /^\d{6,14}$/.test(raw);
      if (!okLen) return say(dial === '+91' ? 'Enter a valid 10-digit mobile number.' : 'Enter a valid mobile number.', 'error');
      target = dial + raw;
      sendBtn.disabled = true; sendBtn.textContent = 'Sending…'; say('');
      try {
        const fb = await loadFirebase();
        const c = await renderCaptcha('invisible', 'invisible');
        try {
          confirmation = await fb.signInWithPhoneNumber(fb.auth, target, c.verifier);
        } catch (err) {
          try { c.verifier.clear(); } catch (e) { /* ignore */ }
          delete captchas.invisible;
          throw err;
        }
        $('[data-soi-reg-target]', reg).textContent = target;
        codeBox.hidden = false;
        digits.forEach((d) => { d.value = ''; });
        digits[0]?.focus();
        tick(30);
      } catch (err) {
        const code = err && err.code;
        if (code === 'auth/operation-not-allowed' || code === 'auth/unauthorized-domain' || !fbCfg.apiKey) {
          // Mobile verification is not switched on for this address yet: do not block sign-up.
          skipped = true; note.value = target; flag.value = 'no';
          say('Mobile verification is not available right now. You can still create your account.', 'ok');
          sync();
        } else {
          say(fbError(err), 'error');
        }
      } finally {
        sendBtn.disabled = false; sendBtn.textContent = 'Send OTP';
      }
    };
    const verify = async () => {
      const code = digits.map((d) => d.value).join('');
      if (code.length !== digits.length) return say('Enter the full code.', 'error');
      if (!confirmation) return say('Please request a new code.', 'error');
      verifyBtn.disabled = true; verifyBtn.textContent = 'Verifying…';
      try {
        await confirmation.confirm(code);
        try { const fb = await loadFirebase(); await fb.signOut(fb.auth); } catch (e) { /* ignore */ }
        verified = true; note.value = target; flag.value = 'yes';
        codeBox.hidden = true; okEl.hidden = false; say('');
        phone.readOnly = true; sendBtn.hidden = true;
        sync();
      } catch (err) {
        say(fbError(err), 'error');
        digits.forEach((d) => { d.value = ''; });
        digits[0]?.focus();
      } finally {
        verifyBtn.disabled = false; verifyBtn.textContent = 'Verify';
      }
    };
    sendBtn.addEventListener('click', send);
    resendBtn.addEventListener('click', send);
    verifyBtn.addEventListener('click', verify);
    changeBtn.addEventListener('click', () => {
      codeBox.hidden = true; confirmation = null; clearInterval(timer); say('');
      phone.readOnly = false; sendBtn.hidden = false; okEl.hidden = true;
      verified = false; skipped = false; note.value = ''; flag.value = 'no'; sync(); phone.focus();
    });
    phone.addEventListener('input', () => {
      phone.value = phone.value.replace(/\D/g, '').slice(0, phone.maxLength > 0 ? phone.maxLength : 14);
      if (verified) return;
      skipped = false; sync();
    });
    digits.forEach((input, i) => {
      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 1);
        if (input.value && digits[i + 1]) digits[i + 1].focus();
        if (digits.every((d) => d.value)) verify();
      });
      input.addEventListener('keydown', (e) => { if (e.key === 'Backspace' && !input.value && digits[i - 1]) digits[i - 1].focus(); });
      input.addEventListener('paste', (e) => {
        const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
        if (!text) return;
        e.preventDefault();
        digits.forEach((d, n) => { d.value = text[n] || ''; });
        (digits.find((d) => !d.value) || digits[digits.length - 1]).focus();
        if (digits.every((d) => d.value)) verify();
      });
    });
    const terms = $('[data-soi-terms]', form || root);
    const comms = $('[data-soi-comms]', form || root);
    const commsEmail = $('[data-soi-comms-email]', form || root);
    const syncComms = () => { if (commsEmail) commsEmail.disabled = !(comms && comms.checked); };
    comms?.addEventListener('change', syncComms);
    syncComms();
    form?.addEventListener('submit', (e) => {
      if (!(verified || skipped)) {
        e.preventDefault();
        say('Please verify your mobile number to continue.', 'error');
        phone.focus();
        return;
      }
      if (terms && !terms.checked) {
        e.preventDefault();
        say('Please agree to the Terms & Conditions and Privacy Policy to continue.', 'error');
        terms.focus();
      }
    });
    sync();
  }

  /* ---------- OTP ---------- */
  const otp = $('[data-soi-otp]', root);
  if (otp) {
    const proxy = (otp.dataset.proxy || '/apps/soi').replace(/\/$/, '');
    const getDial = () => otp.dataset.dial || '+91';
    const msg = $('[data-soi-otp-msg]', otp);
    const digits = $$('[data-soi-otp-digit]', otp);
    const steps = { request: $('[data-soi-otp-step="request"]', otp), verify: $('[data-soi-otp-step="verify"]', otp) };
    let channel = otp.dataset.channel === 'email' ? 'email' : 'sms';
    let target = '';
    let timer;

    const say = (text, kind) => {
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = !text;
      msg.className = `soi-acc__msg${kind ? ' is-' + kind : ''}`;
    };
    const busy = (btn, on, label) => {
      if (!btn) return;
      btn.disabled = on;
      if (on) { btn.dataset.label = btn.textContent; btn.textContent = label; }
      else if (btn.dataset.label) btn.textContent = btn.dataset.label;
    };
    const post = async (path, body) => {
      const res = await fetch(`${proxy}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || 'Something went wrong. Please try again.');
      return data;
    };
    const currentTarget = () => {
      if (channel === 'email') return ($('[data-soi-otp-email]', otp)?.value || '').trim().toLowerCase();
      const raw = ($('[data-soi-otp-phone]', otp)?.value || '').replace(/\D/g, '');
      return raw ? getDial() + raw : '';
    };
    const countdown = (seconds) => {
      const btn = $('[data-soi-otp-resend]', otp);
      const out = $('[data-soi-otp-timer]', otp);
      clearInterval(timer);
      let left = seconds;
      const tick = () => {
        if (left <= 0) { clearInterval(timer); btn.disabled = false; out.textContent = ''; return; }
        btn.disabled = true;
        out.textContent = `in ${left}s`;
        left -= 1;
      };
      tick();
      timer = setInterval(tick, 1000);
    };

    $$('[data-soi-otp-channel]', otp).forEach((btn) => btn.addEventListener('click', () => {
      channel = btn.dataset.soiOtpChannel;
      $$('[data-soi-otp-channel]', otp).forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', String(on));
      });
      $$('[data-soi-otp-field]', otp).forEach((f) => { f.hidden = f.dataset.soiOtpField !== channel; });
      say('');
    }));

    const useFirebase = () => otp.dataset.provider === 'firebase' && channel === 'sms';
    let confirmation = null;
    const sendGate = $('[data-soi-otp-send]', otp);
    const syncSendGate = () => {
      if (!sendGate || !useFirebase()) return;
      const needs = captchaOn && activeTab() === 'signup' && captchas.otp;
      sendGate.disabled = !!needs && !captchas.otp.solved;
    };
    gates.push(syncSendGate);
    $$('[data-soi-auth-tab]', root).forEach((t) => t.addEventListener('click', () => setTimeout(syncSendGate, 0)));
    $$('[data-soi-otp-channel]', otp).forEach((b) => b.addEventListener('click', () => setTimeout(syncSendGate, 0)));

    const fbSend = async (resend) => {
      const fb = await loadFirebase();
      // Sign-up shows the visible check (solved before the button enables); resends and sign-in use an invisible one.
      const visible = !resend && captchaOn && activeTab() === 'signup' && captchas.otp;
      const name = visible ? 'otp' : 'invisible';
      const c = visible ? captchas.otp : await renderCaptcha('invisible', 'invisible');
      try {
        confirmation = await fb.signInWithPhoneNumber(fb.auth, target, c.verifier);
      } catch (err) {
        if (name === 'invisible') { try { c.verifier.clear(); } catch (e) { /* ignore */ } delete captchas.invisible; }
        throw new Error(fbError(err));
      } finally {
        if (name === 'otp') resetCaptcha('otp');
      }
      return { resendIn: Number(otp.dataset.resend) || 30 };
    };
    const fbVerify = async (code) => {
      if (!confirmation) throw new Error('Please ask for a new code.');
      let idToken;
      try {
        const cred = await confirmation.confirm(code);
        idToken = await cred.user.getIdToken();
      } catch (err) { throw new Error(fbError(err)); }
      const data = await post('/auth/otp/firebase', {
        idToken,
        intent: activeTab() === 'signup' ? 'signup' : 'signin',
        marketing: !!$('[data-soi-otp-consent]', otp)?.checked,
        dob: dobValue(),
      });
      try { const fb = await loadFirebase(); await fb.signOut(fb.auth); } catch (e) { /* session is ours, not Firebase's */ }
      return data;
    };

    const otpLive = root.dataset.otpLive === 'true';
    const dobValue = () => ($('[data-soi-otp-dob]', otp)?.value || '').trim();
    const send = async (resend) => {
      if (!otpLive) {
        say('Mobile OTP sign-in is being set up. Please use the Email option for now.', 'error');
        setTimeout(() => { $('[data-soi-method="email"]', root)?.click(); }, 1800);
        return;
      }
      if (activeTab() === 'signup' && !dobValue()) return say('Please enter your date of birth.', 'error');
      target = currentTarget();
      if (channel === 'sms' && !/^\+\d{8,15}$/.test(target)) return say('Enter a valid mobile number.', 'error');
      if (channel === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(target)) return say('Enter a valid email address.', 'error');
      const btn = resend ? $('[data-soi-otp-resend]', otp) : $('[data-soi-otp-send]', otp);
      busy(btn, true, 'Sending…');
      try {
        const data = useFirebase() ? await fbSend(resend) : await post('/auth/otp/send', {
          channel, target,
          intent: $('[data-soi-auth-tab].is-active', root)?.dataset.soiAuthTab === 'signup' ? 'signup' : 'signin',
          marketing: !!$('[data-soi-otp-consent]', otp)?.checked,
          dob: dobValue(),
        });
        steps.request.hidden = true;
        steps.verify.hidden = false;
        $('[data-soi-otp-target]', otp).textContent = target;
        digits.forEach((d) => { d.value = ''; });
        digits[0]?.focus();
        countdown(data.resendIn || 30);
        say(data.testCode ? `Test mode — your code is ${data.testCode}` : '', data.testCode ? 'ok' : null);
      } catch (err) {
        say(err.message, 'error');
      } finally {
        busy(btn, false);
        syncSendGate();
      }
    };

    $('[data-soi-otp-send]', otp)?.addEventListener('click', () => send(false));
    $('[data-soi-otp-resend]', otp)?.addEventListener('click', () => send(true));
    $('[data-soi-otp-edit]', otp)?.addEventListener('click', () => {
      steps.verify.hidden = true;
      steps.request.hidden = false;
      clearInterval(timer);
      say('');
    });

    digits.forEach((input, i) => {
      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 1);
        if (input.value && digits[i + 1]) digits[i + 1].focus();
        if (digits.every((d) => d.value)) $('[data-soi-otp-verify]', otp)?.click();
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Backspace' && !input.value && digits[i - 1]) digits[i - 1].focus();
      });
      input.addEventListener('paste', (e) => {
        const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '');
        if (!text) return;
        e.preventDefault();
        digits.forEach((d, n) => { d.value = text[n] || ''; });
        (digits.find((d) => !d.value) || digits[digits.length - 1]).focus();
        if (digits.every((d) => d.value)) $('[data-soi-otp-verify]', otp)?.click();
      });
    });

    // Android/Chrome one-tap SMS code, where the browser supports it.
    if ('OTPCredential' in window && channel === 'sms') {
      const ac = new AbortController();
      navigator.credentials.get({ otp: { transport: ['sms'] }, signal: ac.signal })
        .then((cred) => {
          if (!cred || !cred.code) return;
          cred.code.split('').forEach((c, n) => { if (digits[n]) digits[n].value = c; });
          $('[data-soi-otp-verify]', otp)?.click();
        })
        .catch(() => {});
      window.addEventListener('beforeunload', () => ac.abort());
    }

    // Spend the single-use password the app returns on Shopify's own login form. This must go through the
    // real {% form 'customer_login' %} on the page (a normal submit event) so Shopify's spam-protection script
    // can attach its captcha token; a hand-built form.submit() is rejected with "Missing CAPTCHA token".
    // Shopify's script silently cancels a submit until it has seen a focus/change event on that form and bound
    // hCaptcha to it, and the OTP digits are not inside that form, so we bind it ourselves (and early).
    const realLogin = () => $('[data-soi-auth-pane="signin"] form[action*="/account/login"]', root);
    const bindCaptcha = () => {
      const f = realLogin();
      const field = f && f.querySelector('[name="customer[email]"]');
      if (field) field.dispatchEvent(new Event('change', { bubbles: true }));
    };
    bindCaptcha();
    const signIn = (email, password, returnUrl) => {
      const real = realLogin();
      const back = returnUrl || new URLSearchParams(location.search).get('return_url') || '/account';
      if (real) {
        const set = (name, value) => {
          let i = real.querySelector(`[name="${name}"]`);
          if (!i) { i = document.createElement('input'); i.type = 'hidden'; i.name = name; real.appendChild(i); }
          i.value = value;
        };
        set('customer[email]', email);
        set('customer[password]', password);
        set('return_url', back);
        bindCaptcha();
        // wait (max ~8s) for Shopify to bind its captcha; if it never does, the store has no captcha on this form
        let tries = 0;
        const go = () => {
          if (real.dataset.hcaptchaBound || real.dataset.recaptchaBound || tries++ > 40) {
            if (typeof real.requestSubmit === 'function') real.requestSubmit();
            else (real.querySelector('[type="submit"]') || {}).click?.();
            return;
          }
          setTimeout(go, 200);
        };
        go();
        return;
      }
      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/account/login';
      form.hidden = true;
      const add = (name, value) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = name; i.value = value;
        form.appendChild(i);
      };
      add('form_type', 'customer_login');
      add('utf8', '✓');
      add('customer[email]', email);
      add('customer[password]', password);
      add('return_url', back);
      document.body.appendChild(form);
      form.submit();
    };

    $('[data-soi-otp-verify]', otp)?.addEventListener('click', async () => {
      const code = digits.map((d) => d.value).join('');
      if (code.length !== digits.length) return say('Enter the full code.', 'error');
      const btn = $('[data-soi-otp-verify]', otp);
      busy(btn, true, 'Verifying…');
      try {
        const data = useFirebase() ? await fbVerify(code) : await post('/auth/otp/verify', { channel, target, code });
        say('Verified — signing you in…', 'ok');
        signIn(data.email, data.password, data.returnUrl);
      } catch (err) {
        say(err.message, 'error');
        digits.forEach((d) => { d.value = ''; });
        digits[0]?.focus();
        busy(btn, false);
      }
    });
  }

  /* ---------- Google ---------- */
  const google = $('[data-soi-google]', root);
  if (google) {
    google.addEventListener('click', () => {
      const proxy = ($('[data-soi-otp]', root)?.dataset.proxy || '/apps/soi').replace(/\/$/, '');
      const back = new URLSearchParams(location.search).get('return_url') || '/account';
      location.href = `${proxy}/auth/google/start?return_url=${encodeURIComponent(back)}`;
    });
  }

  /* ---------- Address book ---------- */
  const openForm = (id) => {
    $$('[data-soi-address-form]', root).forEach((f) => { f.hidden = f.dataset.soiAddressForm !== id; });
  };
  $$('[data-soi-address-new]', root).forEach((b) => b.addEventListener('click', () => {
    openForm('new');
    $('[data-soi-address-form="new"] input')?.focus();
  }));
  $$('[data-soi-address-edit]', root).forEach((b) => b.addEventListener('click', () => openForm(b.dataset.soiAddressEdit)));
  $$('[data-soi-address-cancel]', root).forEach((b) => b.addEventListener('click', () => openForm('__none__')));
  $$('[data-soi-address-delete]', root).forEach((b) => b.addEventListener('click', () => {
    if (!window.confirm(b.dataset.confirm || 'Delete this address?')) return;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = b.dataset.url;
    form.hidden = true;
    const method = document.createElement('input');
    method.type = 'hidden'; method.name = '_method'; method.value = 'delete';
    form.appendChild(method);
    document.body.appendChild(form);
    form.submit();
  }));

  // Country → state list, using Shopify's own province data on each option.
  $$('[data-soi-address-country]', root).forEach((country) => {
    const wrap = country.closest('.soi-acc__row') || root;
    const province = $('[data-soi-address-province]', wrap);
    const provinceWrap = $('[data-soi-address-province-wrap]', wrap);
    if (!province) return;
    const fill = () => {
      const opt = country.options[country.selectedIndex];
      let list = [];
      try { list = JSON.parse(opt?.dataset.provinces || '[]'); } catch (e) { list = []; }
      province.innerHTML = '';
      if (!list.length) {
        if (provinceWrap) provinceWrap.hidden = true;
        return;
      }
      if (provinceWrap) provinceWrap.hidden = false;
      list.forEach(([value, label]) => {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        if (value === province.dataset.default) o.selected = true;
        province.appendChild(o);
      });
    };
    if (country.dataset.default) country.value = country.dataset.default;
    fill();
    country.addEventListener('change', fill);
  });
})();
